/**
 * Shared attachment upload for Forum + Staff Room.
 * Uploads to /api/academy/community/upload, returns the attachment envelope
 * ({url, kind, filename, content_type, size, storage_path}) that goes into
 * a post/message's `attachments[]` field.
 */
import { useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

export const useAttachments = () => {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const addFile = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/academy/community/upload', fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setAttachments((prev) => [...prev, r.data]);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  }, []);

  const removeAt = useCallback((idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const reset = useCallback(() => setAttachments([]), []);

  return { attachments, uploading, addFile, removeAt, reset };
};

/** Renders an attachment (image or video). Auth via `?auth=` query param
 *  since <img>/<video> tags can't send Authorization headers. */
export const AttachmentPreview = ({ att, onRemove }) => {
  const token = localStorage.getItem('academy_token') || '';
  const src = att.url + (att.url.includes('?') ? '&' : '?') + `auth=${encodeURIComponent(token)}`;
  return (
    <div className="relative border-2 border-zinc-800 overflow-hidden group" style={{ maxWidth: 240 }}>
      {att.kind === 'video' ? (
        <video src={src} controls className="w-full max-h-48 bg-black" preload="metadata" />
      ) : (
        <img src={src} alt={att.filename || 'attachment'} className="w-full max-h-48 object-cover" loading="lazy" />
      )}
      {onRemove && (
        <button
          onClick={onRemove}
          className="absolute top-1 right-1 bg-black/80 border border-red-500/60 text-red-400 hover:text-white hover:bg-red-500 text-[10px] px-1.5 py-0.5 font-black uppercase tracking-widest"
          data-testid="attachment-remove"
        >
          Remove
        </button>
      )}
      {att.filename && <div className="text-[9px] uppercase tracking-widest text-zinc-500 px-1.5 py-1 truncate bg-zinc-950">{att.filename}</div>}
    </div>
  );
};
